import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ToolbarIconButton } from '@/design-system'
import ZoomInIcon from '@mui/icons-material/AddOutlined'
import ZoomOutIcon from '@mui/icons-material/RemoveOutlined'
import CenterFocusIcon from '@mui/icons-material/CenterFocusWeakOutlined'
import PatternPreview from '@/pages/pattern/Preview'
import { IPatternAssets, PatternDraft, PatternProps, PatternSelections } from '@/types'

export interface StudioCanvasStageProps {
  canvaRef: React.Ref<any>
  assets: IPatternAssets
  selection: PatternSelections
  selectedTab: string
  patternData: PatternDraft
  isEditMode: boolean
  onEdit: (data: Omit<PatternDraft, 'preview'>) => void
  onPatternRestore: (p: PatternProps) => void
  onScreenshotReady: (file: File) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onRecenter: () => void
}

const Root = styled(Box)({
  flex: 1,
  display: 'flex',
  position: 'relative',
  minWidth: 0,
  backgroundImage:
    'radial-gradient(circle, var(--ds-border) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  backgroundColor: 'var(--ds-canvas)',
  // Preview.tsx wraps the canvas in its own `<Box position="relative"
  // flex={1}>` — that `flex={1}` only takes effect (and gives the wrapper a
  // definite height) if ITS parent is a flex container, so `display: flex`
  // here is load-bearing, not decorative. Without it the wrapper collapses
  // to 0 height (the canvas inside is `position: absolute`, contributing no
  // in-flow height), and the wrapper is the canvas's actual containing
  // block for the `#renderingCanvas` override below.
  '& #renderingCanvas': {
    // renderer/main.js's SetCanvasMode('edit') hardcodes #renderingCanvas's
    // inline top/height to the legacy /pattern page's 69px top+bottom bars —
    // this Studio's topbar/filmstrip reserve their own flex space at
    // different heights, so those legacy offsets just hang the canvas over
    // the filmstrip. Force it to fill its (now correctly sized) container
    // instead, regardless of which mode last set inline styles.
    top: '0 !important',
    left: '0 !important',
    width: '100% !important',
    height: '100% !important',
  },
})

const ZoomPill = styled(Box)({
  position: 'absolute',
  bottom: 'var(--ds-space-4)',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: 6,
  borderRadius: 'var(--ds-radius-pill)',
  backgroundColor: 'var(--ds-surface)',
  boxShadow: 'var(--ds-shadow-lift)',
  zIndex: 6,
})

/** Frames the unmodified Babylon PatternPreview canvas with studio chrome */
export function StudioCanvasStage({
  canvaRef,
  assets,
  selection,
  selectedTab,
  patternData,
  isEditMode,
  onEdit,
  onPatternRestore,
  onScreenshotReady,
  onZoomIn,
  onZoomOut,
  onRecenter,
}: StudioCanvasStageProps) {
  return (
    <Root>
      <PatternPreview
        ref={canvaRef}
        selection={selection}
        selectedTab={selectedTab}
        assets={assets}
        patternData={patternData}
        textData=''
        isEditMode={isEditMode}
        isPagePreview={false}
        addScreenshot={onScreenshotReady}
        onPatternRestore={onPatternRestore}
        onAssetsUpdate={() => undefined}
        onToggleFullScreenMode={() => undefined}
        onEdit={onEdit}
        toggleSliderVisibilityCallBack={() => undefined}
        updateSliderFromAccentScaleCallBack={() => undefined}
        updateSliderFromAccentNumberCallBack={() => undefined}
        updateSliderFromAccentRotationCallBack={() => undefined}
        updateSliderFromPrintTilingCallBack={() => undefined}
        updateSliderFromCameraHorizontalCallback={() => undefined}
        onResetTemplate={() => undefined}
      />

      <ZoomPill>
        <ToolbarIconButton icon={<ZoomOutIcon sx={{ fontSize: 18 }} />} label='Zoom out' onClick={onZoomOut} />
        <ToolbarIconButton icon={<CenterFocusIcon sx={{ fontSize: 18 }} />} label='Recenter camera' onClick={onRecenter} />
        <ToolbarIconButton icon={<ZoomInIcon sx={{ fontSize: 18 }} />} label='Zoom in' onClick={onZoomIn} />
      </ZoomPill>
    </Root>
  )
}
